// src/markup/pages/programmatic-service-page.jsx
import React, { Component } from 'react';
import { Redirect, withRouter, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import Modal from '../elements/modal';
import { getLocalItem, getSessionItem } from '../../utils/storage';
import { getBikeTiers, getCarTiers, getServiceStartingPrice, formatInr } from '../../utils/pricing';
import { resolveBrandVehicleImage } from '../../utils/brandLeadModels';

// Premium components
import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  QuickFactsPanel,
  BlogCard,
  StructuredData,
  RelatedTopServices,
} from '../elements/premium';

// ─── STATIC DATA ─────────────────────────────────────────────────
const TRUST_ITEMS = [
  { value: '2,00,000+', label: 'Customers Served' },
  { value: '4.8\u2605', label: 'Customer Rating' },
  { value: '32+', label: 'Cities in India' },
  { value: '30-Day', label: 'Service Warranty' },
];

const STEPS = [
  { no: '01', icon: '\uD83D\uDCF1', title: 'Book Online', desc: 'Select your vehicle, choose a service, pick a time slot. Takes under 60 seconds.' },
  { no: '02', icon: '\uD83D\uDE90', title: 'Mechanic Arrives', desc: 'Our certified, uniformed mechanic arrives at your doorstep \u2014 on time, every time.' },
  { no: '03', icon: '\uD83D\uDD27', title: 'Service Done', desc: 'Professional service with genuine parts. Real-time updates and photos shared via app.' },
  { no: '04', icon: '\u2705', title: 'Warranty Included', desc: 'Digital job card, before/after photos, and a 30-day service warranty \u2014 guaranteed.' },
];

const WHY_US_ITEMS = [
  { icon: '\uD83C\uDFE0', title: 'Doorstep Service', desc: 'Mechanic comes to your home or office \u2014 no need to visit a workshop or wait in queues.' },
  { icon: '\uD83C\uDF93', title: 'Certified Mechanics', desc: 'Every mechanic is background-verified, brand-trained, and rated 4.8\u2605 by customers.' },
  { icon: '\uD83D\uDCB0', title: 'Transparent Pricing', desc: 'See the full price before you book. No hidden charges, no surprises \u2014 ever.' },
  { icon: '\uD83D\uDD12', title: 'Genuine Parts', desc: 'OEM-grade and genuine spare parts only. Your manufacturer warranty stays intact.' },
  { icon: '\uD83D\uDCF8', title: 'Photo Updates', desc: 'Before & after photos of every job shared in real time via app and WhatsApp.' },
  { icon: '\uD83D\uDEE1\uFE0F', title: '30-Day Warranty', desc: 'Every service backed by a 30-day warranty on parts and labour. No questions asked.' },
];

// ─── SERVICE CHECKLISTS ──────────────────────────────────────────
const BIKE_CHECKLIST = [
  { item: 'Engine oil change',              basic: true,  standard: true,  comp: true },
  { item: 'Oil filter replacement',         basic: false, standard: true,  comp: true },
  { item: 'Air filter cleaning',            basic: true,  standard: true,  comp: true },
  { item: 'Air filter replacement',         basic: false, standard: false, comp: true },
  { item: 'Spark plug check',               basic: true,  standard: true,  comp: true },
  { item: 'Spark plug replacement',         basic: false, standard: false, comp: true },
  { item: 'Chain cleaning & lubrication',   basic: true,  standard: true,  comp: true },
  { item: 'Chain adjustment',               basic: false, standard: true,  comp: true },
  { item: 'Brake pad inspection',           basic: true,  standard: true,  comp: true },
  { item: 'Brake fluid top-up',             basic: false, standard: true,  comp: true },
  { item: 'Clutch adjustment',              basic: false, standard: true,  comp: true },
  { item: 'Throttle cable lubrication',     basic: false, standard: true,  comp: true },
  { item: 'Battery terminal cleaning',      basic: true,  standard: true,  comp: true },
  { item: 'Battery health check',           basic: false, standard: true,  comp: true },
  { item: 'Tyre pressure check',            basic: true,  standard: true,  comp: true },
  { item: 'Coolant top-up (liquid-cooled)', basic: false, standard: false, comp: true },
  { item: 'Full electrical check',          basic: false, standard: false, comp: true },
  { item: 'Carb/FI tuning',                basic: false, standard: false, comp: true },
  { item: 'Washing & polish',              basic: false, standard: false, comp: true },
];

const CAR_CHECKLIST = [
  { item: 'Engine oil change',              basic: true,  standard: true,  comp: true },
  { item: 'Oil filter replacement',         basic: true,  standard: true,  comp: true },
  { item: 'Air filter inspection',          basic: true,  standard: true,  comp: true },
  { item: 'Air filter replacement',         basic: false, standard: true,  comp: true },
  { item: 'Cabin/AC filter check',          basic: false, standard: true,  comp: true },
  { item: 'Cabin/AC filter replacement',    basic: false, standard: false, comp: true },
  { item: 'Brake pad inspection',           basic: true,  standard: true,  comp: true },
  { item: 'Brake fluid top-up',             basic: false, standard: true,  comp: true },
  { item: 'Coolant top-up',                 basic: true,  standard: true,  comp: true },
  { item: 'Battery health check',           basic: true,  standard: true,  comp: true },
  { item: 'Spark plug check',               basic: false, standard: true,  comp: true },
  { item: 'Wiper fluid top-up',             basic: true,  standard: true,  comp: true },
  { item: 'Tyre pressure + rotation',       basic: true,  standard: true,  comp: true },
  { item: 'Wheel alignment check',          basic: false, standard: false, comp: true },
  { item: 'Suspension inspection',          basic: false, standard: true,  comp: true },
  { item: 'AC gas pressure check',          basic: false, standard: false, comp: true },
  { item: 'Exhaust system check',           basic: false, standard: false, comp: true },
  { item: 'Transmission fluid check',       basic: false, standard: false, comp: true },
  { item: 'Full electrical diagnostics',    basic: false, standard: false, comp: true },
  { item: 'Interior vacuum + exterior wash',basic: false, standard: true,  comp: true },
  { item: 'Underbody rust treatment',       basic: false, standard: false, comp: true },
  { item: 'Engine flush',                   basic: false, standard: false, comp: true },
];

// Tiers are derived from pricing-snapshot (backend SoT) at render time
// so a change to backend pricing reflects automatically on next build.

// ─── HELPERS ─────────────────────────────────────────────────────
function titleCase(str) {
  if (!str) return '';
  return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Pull the model names out of trust.brand_note, e.g.
// "All BMW mechanics are trained on 3 Series, 5 Series, X1, X3 and X5"
// -> ['3 Series','5 Series','X1','X3','X5']. Returns [] if it can't parse.
function extractModels(brandNote) {
  if (!brandNote || typeof brandNote !== 'string') return [];
  const m = brandNote.match(/trained on (.+?)\.?$/i);
  if (!m) return [];
  return m[1]
    .replace(/\band\b/gi, ',')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .slice(0, 6);
}

function getPageDataFromWindow() {
  if (typeof window !== 'undefined' && window.__PROGRAMMATIC_PAGE_DATA__) {
    return window.__PROGRAMMATIC_PAGE_DATA__;
  }
  if (typeof global !== 'undefined' && global.__PROGRAMMATIC_PAGE_DATA__) {
    return global.__PROGRAMMATIC_PAGE_DATA__;
  }
  return null;
}

// ─── COMPONENT ───────────────────────────────────────────────────
class ProgrammaticServicePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageData: getPageDataFromWindow(),
      loading: !getPageDataFromWindow(),
      notFound: false,
    };
  }

  componentDidMount() {
    const { setOpenModal, isMobileApp, history, user } = this.props;

    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
      return;
    }

    // If data was provided by SSR, clean up the global and we're done
    if (this.state.pageData) {
      if (typeof window !== 'undefined') {
        delete window.__PROGRAMMATIC_PAGE_DATA__;
      }
      return;
    }

    // Client-side navigation: fetch data from API
    this.fetchPageData();
  }

  componentDidUpdate(prevProps) {
    const prevUrl = prevProps.match.url;
    const currUrl = this.props.match.url;
    if (prevUrl !== currUrl) {
      this.setState({ loading: true, notFound: false, pageData: null });
      this.fetchPageData();
    }
  }

  fetchPageData() {
    const { service, brand, city } = this.getRouteParams();
    fetch(`/_data/programmatic-page/${service}/${brand}/${city}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        this.setState({ pageData: data, loading: false });
      })
      .catch(() => {
        this.setState({ notFound: true, loading: false });
      });
  }

  getRouteParams() {
    const { match, location } = this.props;
    if (match && match.params && match.params.brand) {
      // Extract service from the pathname (first segment)
      const pathParts = location.pathname.split('/').filter(Boolean);
      return {
        service: pathParts[0] || match.params.service || '',
        brand: match.params.brand,
        city: match.params.city,
      };
    }
    return { service: '', brand: '', city: '' };
  }

  getBreadcrumbs(data) {
    const svc = data.service || {};
    const brand = data.brand || {};
    const city = data.city || {};
    return [
      { label: 'Home', href: '/' },
      { label: svc.label || titleCase(svc.slug), href: `/${svc.slug || ''}` },
      { label: brand.display || titleCase(brand.slug) },
      { label: city.display || titleCase(city.slug) },
    ];
  }

  getRelatedBlogs() {
    const { blogsData } = this.props;
    if (!blogsData || !blogsData.length) return [];
    return blogsData.slice(0, 3);
  }

  renderChecklist(vehicleType) {
    const isBike = vehicleType === 'bike';
    const checklist = isBike ? BIKE_CHECKLIST : CAR_CHECKLIST;
    const tiers = isBike ? getBikeTiers() : getCarTiers();

    return (
      <div className="rr-prog__checklist">
        <div className="rr-prog__checklist-wrap">
          <table className="rr-prog__table">
            <thead>
              <tr>
                <th>Service Check</th>
                <th className="rr-prog__table-tier--basic">
                  <span className="rr-prog__tier-label">{tiers.basic.label}</span>
                  <span className="rr-prog__tier-price">{tiers.basic.price}</span>
                </th>
                <th className="rr-prog__table-tier--standard">
                  <span className="rr-prog__tier-label">{tiers.standard.label}</span>
                  <span className="rr-prog__tier-price">{tiers.standard.price}</span>
                  <span className="rr-prog__recommended">Recommended</span>
                </th>
                <th className="rr-prog__table-tier--comprehensive">
                  <span className="rr-prog__tier-label">{tiers.comp.label}</span>
                  <span className="rr-prog__tier-price">{tiers.comp.price}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {checklist.map((row, i) => (
                <tr key={i}>
                  <td>{row.item}</td>
                  <td className="rr-prog__table-col--basic">
                    {row.basic
                      ? <span className="rr-prog__check">{'\u2713'}</span>
                      : <span className="rr-prog__dash">{'\u2014'}</span>}
                  </td>
                  <td className="rr-prog__table-col--standard">
                    {row.standard
                      ? <span className="rr-prog__check">{'\u2713'}</span>
                      : <span className="rr-prog__dash">{'\u2014'}</span>}
                  </td>
                  <td className="rr-prog__table-col--comprehensive">
                    {row.comp
                      ? <span className="rr-prog__check">{'\u2713'}</span>
                      : <span className="rr-prog__dash">{'\u2014'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderContent(content) {
    if (!content) return null;
    const why = content.why_ride_n_repair_in_city || [];
    const trust = content.trust || {};
    const models = extractModels(trust.brand_note);

    return (
      <div className="rr-prog__content">
        {/* Lead paragraph — larger, centred, the page's opening hook */}
        {content.city_brand_intro && (
          <p className="rr-prog__lead">{content.city_brand_intro}</p>
        )}

        {/* Two-column: Why-RnR copy on the left, brand-expertise pull-quote right */}
        <div className="rr-prog__split">
          <div className="rr-prog__split-main">
            {why.length > 0 && <h3>Why Ride N Repair?</h3>}
            {why.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {content.brand_specific_service_detail && (
            <aside className="rr-prog__pullquote">
              <span className="rr-prog__pullquote-tag">Brand-Specific Expertise</span>
              <p>{content.brand_specific_service_detail}</p>
            </aside>
          )}
        </div>

        {/* Brand-models chip strip — proof we know the customer's exact model */}
        {models.length > 0 && (
          <div className="rr-prog__models">
            <span className="rr-prog__models-label">Mechanics trained on</span>
            {models.map((m, i) => (
              <span key={i} className="rr-prog__model-chip">{m}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderServiceScope(scope) {
    if (!scope || !scope.length) return null;
    return (
      <div className="rr-prog__scope-list">
        {scope.map((item, i) => (
          <div key={i} className="rr-prog__scope-item">{item}</div>
        ))}
      </div>
    );
  }

  renderAreasCovered(areas) {
    if (!areas || !areas.length) return null;
    return (
      <div className="rr-prog__areas">
        {areas.map((area, i) => (
          <span key={i} className="rr-prog__area-tag">{area}</span>
        ))}
      </div>
    );
  }

  renderRelatedLinks(related) {
    if (!related) return null;
    const { related_cities, related_brands, related_services } = related;
    const hasAnything = (related_cities && related_cities.length > 0) ||
                        (related_brands && related_brands.length > 0) ||
                        (related_services && related_services.length > 0);
    if (!hasAnything) return null;

    return (
      <div>
        {related_cities && related_cities.length > 0 && (
          <div className="rr-prog__related-grid">
            <div className="rr-prog__related-grid-title">Same service in other cities</div>
            <div className="rr-prog__related-pills">
              {related_cities.map((rc, i) => (
                <Link key={i} to={rc.url} className="rr-prog__related-pill">{rc.display || titleCase(rc.city)}</Link>
              ))}
            </div>
          </div>
        )}

        {related_brands && related_brands.length > 0 && (
          <div className="rr-prog__related-grid">
            <div className="rr-prog__related-grid-title">Other brands in this city</div>
            <div className="rr-prog__related-pills">
              {related_brands.map((rb, i) => (
                <Link key={i} to={rb.url} className="rr-prog__related-pill">{rb.display || titleCase(rb.brand)}</Link>
              ))}
            </div>
          </div>
        )}

        {related_services && related_services.length > 0 && (
          <div className="rr-prog__related-grid">
            <div className="rr-prog__related-grid-title">Other services for this brand</div>
            <div className="rr-prog__related-pills">
              {related_services.map((rs, i) => (
                <Link key={i} to={rs.url} className="rr-prog__related-pill">{rs.display || titleCase(rs.service)}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  buildSchemas(data) {
    const canonical = `https://www.ridenrepair.com${data.url || data.canonical || '/' + data.slug}`;
    const brandDisplay = data.brand && data.brand.display ? data.brand.display : '';
    const cityDisplay = data.city && data.city.display ? data.city.display : '';
    const serviceLabel = data.service && data.service.label ? data.service.label : '';
    const serviceSlug = (data.service && data.service.slug) || '';
    const vehicleType = data.vehicle_type || 'bike';

    // Self-heal price from the snapshot (same source as the hero) so schema can
    // never diverge from what the customer sees, even if a JSON is stale.
    const sp = getServiceStartingPrice(serviceSlug, vehicleType);
    const lowPrice = sp && sp.amount ? sp.amount : (data.hero && data.hero.starting_price) || null;
    const highPrice = sp && sp.highForRange ? sp.highForRange : null;
    const priceRange = (sp && sp.rangeLabel)
      || (data.schema && data.schema.price_range) || '';

    const schemas = [];

    // Service schema
    schemas.push({
      '@type': 'Service',
      name: `${brandDisplay} ${serviceLabel} in ${cityDisplay}`,
      serviceType: serviceLabel,
      provider: {
        '@type': 'Organization',
        '@id': 'https://www.ridenrepair.com/#organization',
        name: 'Ride N Repair',
        url: 'https://www.ridenrepair.com',
        logo: 'https://www.ridenrepair.com/images/logo.webp',
        telephone: '+91-120-361-5050',
      },
      description: data.meta ? data.meta.description : '',
      areaServed: {
        '@type': 'City',
        name: cityDisplay,
      },
      ...(priceRange ? { priceRange } : {}),
      ...(lowPrice ? {
        offers: (highPrice && highPrice !== lowPrice) ? {
          '@type': 'AggregateOffer',
          priceCurrency: 'INR',
          lowPrice: String(lowPrice),
          highPrice: String(highPrice),
          availability: 'https://schema.org/InStock',
          url: canonical,
        } : {
          '@type': 'Offer',
          price: String(lowPrice),
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url: canonical,
        },
      } : {}),
    });

    // FAQPage schema
    const faqs = data.content && data.content.faq ? data.content.faq : [];
    if (faqs.length > 0) {
      schemas.push({
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    // BreadcrumbList schema
    const breadcrumbItems = data.schema && data.schema.breadcrumb ? data.schema.breadcrumb : [];
    if (breadcrumbItems.length > 0) {
      schemas.push({
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((bc, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: bc.name,
          ...(bc.item ? { item: `https://www.ridenrepair.com${bc.item}` } : {}),
        })),
      });
    }

    return schemas;
  }

  render() {
    const { pageData, loading, notFound } = this.state;

    if (notFound) return <Redirect to="/error-404" />;

    if (loading || !pageData) {
      return (
        <div className="rr-home rr-home--lux">
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#999', fontSize: 16 }}>Loading...</p>
          </div>
        </div>
      );
    }

    const {
      isMobileBrowser,
      isMobileApp,
      user,
      setUser,
      setCitiesModal,
      openModal,
      setOpenModal,
    } = this.props;

    const data = pageData;
    const heroData = data.hero || {};
    const content = data.content || {};
    const related = data.related || {};
    const vehicleType = data.vehicle_type || 'bike';
    const brandDisplay = data.brand ? data.brand.display : '';
    const cityDisplay = data.city ? data.city.display : '';
    const serviceLabel = data.service ? data.service.label : '';
    const serviceSlug = (data.service && data.service.slug) || '';
    // Tiered Basic/Standard/Comprehensive package table is only coherent on the
    // general-service pages. Specialised services (oil change, repair, AC,
    // battery) show their own service-specific "what's included" scope instead.
    const isGeneralService = serviceSlug === 'bike-service' || serviceSlug === 'car-service';
    const h1 = heroData.h1 || `${brandDisplay} ${serviceLabel} in ${cityDisplay}`;
    // Always override JSON's starting_price with the snapshot value so
    // stale JSONs render correct prices even before regen-prog-pages catches up.
    const canonicalStart = getServiceStartingPrice(serviceSlug, vehicleType);
    const startingPrice = canonicalStart.label || formatInr(heroData.starting_price);
    const brandSlug = (data.brand && data.brand.slug) || '';
    const heroVehicleImage = resolveBrandVehicleImage(brandSlug, vehicleType);
    const canonical = `https://www.ridenrepair.com${data.url || data.canonical || '/' + data.slug}`;
    const relatedBlogs = this.getRelatedBlogs();
    const schemas = this.buildSchemas(data);

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{data.meta ? data.meta.title : h1}</title>
          <meta name="description" content={data.meta ? data.meta.description : ''} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={data.meta ? data.meta.title : h1} />
          <meta property="og:description" content={data.meta ? data.meta.description : ''} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://www.ridenrepair.com/images/og-default.jpg" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          user={user}
          setUser={setUser}
        />

        {!isMobileApp && openModal && (
          <Modal
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            openModal={openModal}
            setOpenModal={setOpenModal}
            user={user}
            setUser={setUser}
          />
        )}

        {/* HERO */}
        <PremiumHero
          badge="Doorstep Service"
          title={h1}
          priceTag={`Starting ${startingPrice}`}
          subtitle={heroData.subtitle || ''}
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          vehicleImage={heroVehicleImage}
          vehicleImageAlt={brandDisplay ? `${brandDisplay} ${serviceLabel} at your doorstep in ${cityDisplay} — Ride N Repair` : ''}
          ctas={[
            { label: `${heroData.cta || 'Book Now'} \u2014 ${startingPrice} Onwards`, href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
          trustItems={TRUST_ITEMS}
        />

        {/* Trust band removed — PremiumHero trustItems already render inline
            inside the hero. No second band needed below. */}

        {/* BREADCRUMBS — schema suppressed here; buildSchemas emits the single
            complete BreadcrumbList (with leaf item URL) to avoid duplicates. */}
        <Breadcrumbs items={this.getBreadcrumbs(data)} withSchema={false} />

        {/* QUICK FACTS — surfaces the per-page trust data (price/turnaround/
            arrival/warranty) that was previously only in JSON-LD. */}
        <QuickFactsPanel
          facts={[
            { icon: '💰', value: startingPrice, label: 'Starting price' },
            { icon: '⏱️', value: (content.trust && content.trust.turnaround) || '60–90 min', label: 'Typical turnaround' },
            { icon: '🛵', value: '15-min', label: 'Doorstep arrival' },
            { icon: '🛡️', value: '30-Day', label: 'Service warranty' },
          ]}
        />

        {/* STICKY CTA */}
        <StickyMobileCTA
          title={h1}
          priceHint={`Starting ${startingPrice} \u00B7 30-Day Warranty`}
          btnLabel="Book Now"
          btnHref="/service"
        />

        {/* UNIQUE CONTENT */}
        <SectionWrapper kicker="Overview" heading={`${brandDisplay} ${serviceLabel} in ${cityDisplay}`}>
          {this.renderContent(content)}
        </SectionWrapper>

        {/* SERVICE CHECKLIST \u2014 tiered package comparison, general-service pages only.
            On specialised services this table would show the wrong rows/prices. */}
        {isGeneralService && (
          <SectionWrapper variant="gray" kicker="What's Included" heading={`${serviceLabel} Checklist \u2014 ${vehicleType === 'car' ? 'Car' : 'Bike'} Service Packages`}>
            {this.renderChecklist(vehicleType)}
            <div className="rr-text-center rr-mt-32">
              <Link to="/service" className="rr-cta-primary">{`Book Now \u2014 ${startingPrice} Onwards`}</Link>
            </div>
          </SectionWrapper>
        )}

        {/* WHAT'S INCLUDED \u2014 service-specific scope for specialised services
            (oil change, repair, AC, battery). Always correct for the page's service. */}
        {!isGeneralService && content.service_scope && content.service_scope.length > 0 && (
          <SectionWrapper variant="gray" kicker="What's Included" heading={`What's Included in ${brandDisplay} ${serviceLabel}`}>
            {this.renderServiceScope(content.service_scope)}
            <div className="rr-text-center" style={{ marginTop: 20, fontSize: 16, color: '#555' }}>Starting <strong style={{ color: '#f42222' }}>{startingPrice}</strong> &middot; transparent pricing, no hidden charges</div>
            <div className="rr-text-center rr-mt-32">
              <Link to="/service" className="rr-cta-primary">{`Book ${serviceLabel} \u2014 ${startingPrice} Onwards`}</Link>
            </div>
          </SectionWrapper>
        )}

        {/* HOW IT WORKS */}
        <SectionWrapper variant="dark" kicker="Simple Process" heading="How It Works">
          <StepCards steps={STEPS} variant="dark" />
        </SectionWrapper>

        {/* WHY RIDE N REPAIR */}
        <SectionWrapper variant="gray" kicker="Our Promise" heading="Why 2,00,000+ Customers Choose Ride N Repair">
          <WhyUsGrid items={WHY_US_ITEMS} />
        </SectionWrapper>

        {/* AREAS COVERED */}
        {content.areas_covered && content.areas_covered.length > 0 && (
          <SectionWrapper kicker={`Service Areas in ${cityDisplay}`} heading={`${brandDisplay} ${serviceLabel} Available In`}>
            {this.renderAreasCovered(content.areas_covered)}
          </SectionWrapper>
        )}

        {/* FAQs */}
        {content.faq && content.faq.length > 0 && (
          <SectionWrapper narrow kicker="Common Questions" heading={`${brandDisplay} ${serviceLabel} in ${cityDisplay} \u2014 FAQ`}>
            <FaqAccordion faqs={content.faq} withSchema={false} />
          </SectionWrapper>
        )}

        {/* RELATED LINKS — only when there's something to link to */}
        {(related && ((related.related_cities && related.related_cities.length) ||
          (related.related_brands && related.related_brands.length) ||
          (related.related_services && related.related_services.length))) ? (
          <SectionWrapper variant="gray" kicker="Explore More" heading="Related Services">
            {this.renderRelatedLinks(related)}
          </SectionWrapper>
        ) : null}

        {/* RELATED BLOGS */}
        {relatedBlogs.length > 0 && (
          <SectionWrapper kicker="From Our Blog" heading="Expert Tips & Guides">
            <div className="rr-card-grid--3col">
              {relatedBlogs.map((blog, i) => (
                <BlogCard
                  key={i}
                  slug={blog.slug}
                  title={blog.title}
                  excerpt={blog.excerpt || blog.description}
                  image={blog.image || blog.thumbnail}
                  category={blog.category}
                  date={blog.date}
                  readTime={blog.readTime}
                />
              ))}
            </div>
          </SectionWrapper>
        )}

        {/* RELATED HIGH-LTV PAGES — booking-driven internal link graph */}
        <RelatedTopServices variant="generic" kicker="Popular With Customers" heading="Other Doorstep Services Customers Book" />

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Book {brandDisplay} {serviceLabel} in {cityDisplay}<br />Get It Done Today.</>}
          subtitle={`Doorstep service \u00B7 Certified mechanics \u00B7 ${startingPrice} onwards \u00B7 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          setCitiesModal={setCitiesModal}
        />
      </div>
    );
  }
}

export default withRouter(ProgrammaticServicePage);
